import { FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { LiveConfig } from "../multimodal-live-types";

export const APP_NAME = "Earth Day Study Companion";
export const APP_TAGLINE =
  "Climate literacy, teach-ins, and guided action for Earth Day communities.";

export type ExperienceMeta = {
  title: string;
  route: string;
  navLabel: string;
  heroLabel: string;
  description: string;
  eyebrow: string;
};

export type TeachInBrief = {
  sessionTitle: string;
  audience: string;
  venueContext: string;
  duration: string;
  goals: string;
  focusAreas: string;
  materials: string;
};

export const DEFAULT_TEACH_IN_BRIEF: TeachInBrief = {
  sessionTitle: "Neighborhood Clean Energy Teach-In",
  audience:
    "High-school students, parents, and community volunteers curious about practical local climate action",
  venueContext:
    "Public library community room with projector and open discussion seating",
  duration: "45 minutes",
  goals:
    "Help attendees understand clean energy in everyday life, connect it to climate justice, and leave with two realistic actions they can take this month.",
  focusAreas:
    "Renewable energy basics, energy bills, climate justice, neighborhood resilience, community action",
  materials:
    "Projector, whiteboard, local utility bill example, sticky notes, Earth Day handout, signup sheet",
};

export const normalizeTeachInBrief = (
  saved: Partial<TeachInBrief> | null | undefined,
): TeachInBrief => {
  if (!saved) {
    return DEFAULT_TEACH_IN_BRIEF;
  }

  return {
    sessionTitle:
      typeof saved.sessionTitle === "string" && saved.sessionTitle.trim()
        ? saved.sessionTitle.trim()
        : DEFAULT_TEACH_IN_BRIEF.sessionTitle,
    audience:
      typeof saved.audience === "string" && saved.audience.trim()
        ? saved.audience.trim()
        : DEFAULT_TEACH_IN_BRIEF.audience,
    venueContext:
      typeof saved.venueContext === "string" && saved.venueContext.trim()
        ? saved.venueContext.trim()
        : DEFAULT_TEACH_IN_BRIEF.venueContext,
    duration:
      typeof saved.duration === "string" && saved.duration.trim()
        ? saved.duration.trim()
        : DEFAULT_TEACH_IN_BRIEF.duration,
    goals:
      typeof saved.goals === "string" && saved.goals.trim()
        ? saved.goals.trim()
        : DEFAULT_TEACH_IN_BRIEF.goals,
    focusAreas:
      typeof saved.focusAreas === "string" && saved.focusAreas.trim()
        ? saved.focusAreas.trim()
        : DEFAULT_TEACH_IN_BRIEF.focusAreas,
    materials:
      typeof saved.materials === "string" && saved.materials.trim()
        ? saved.materials.trim()
        : DEFAULT_TEACH_IN_BRIEF.materials,
  };
};

export type FacilitatorPlan = {
  summary: string;
  learning_objectives: string[];
  agenda: string[];
  materials: string[];
  community_actions: string[];
};

export type FacilitatorTranscriptTurn = {
  id: string;
  speaker: "user" | "gemini";
  text: string;
  timestamp: string;
  status: "partial" | "final";
};

export type FacilitatorSessionMetrics = {
  duration_seconds: number;
  user_turns: number;
  gemini_turns: number;
  total_turns: number;
};

export type FacilitatorReport = {
  id: string;
  title: string;
  created_at: string;
  readiness_score: number;
  readiness_label: string;
  session_overview: string;
  audience_fit_summary: string;
  strongest_moments: string[];
  improvement_opportunities: string[];
  agenda_refinements: string[];
  materials_preparation: string[];
  follow_up_actions: string[];
  transcript_excerpt: string[];
  transcript: FacilitatorTranscriptTurn[];
  session_metrics: FacilitatorSessionMetrics;
  facilitator_plan: FacilitatorPlan | null;
  pdf_download_url: string;
};

export const TEACH_IN_BRIEF_STORAGE_KEY = "earthDayTeachInBrief";
export const TEACH_IN_REPORT_STORAGE_KEY = "earthDayFacilitatorReport";

export const EXPERIENCES: Record<"study" | "course" | "facilitator", ExperienceMeta> =
  {
    study: {
      title: "Climate Library",
      route: "/study",
      navLabel: "Library",
      heroLabel: "Climate Library",
      eyebrow: "Grounded research",
      description:
        "Upload climate reports, Earth Day toolkits, and sustainability PDFs, then ask grounded questions with source-aware guidance.",
    },
    course: {
      title: "Teach-In Builder",
      route: "/course",
      navLabel: "Builder",
      heroLabel: "Teach-In Builder",
      eyebrow: "Learning journeys",
      description:
        "Turn an Earth Day topic into a structured learning path with modules, mindmaps, practice prompts, and certificate-ready milestones.",
    },
    facilitator: {
      title: "Teach-In Facilitator",
      route: "/facilitator",
      navLabel: "Facilitator",
      heroLabel: "Teach-In Facilitator",
      eyebrow: "Live Gemini planning",
      description:
        "Brief Gemini on your audience and setting, then co-design a practical Earth Day session with goals, agenda, materials, and community actions.",
    },
  };

const BASE_MODEL = "models/gemini-2.5-flash-native-audio-preview-12-2025";

export const facilitatorPlanDeclaration: FunctionDeclaration = {
  name: "generate_facilitator_plan",
  description:
    "Create a structured Earth Day teach-in plan once enough context has been gathered.",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      summary: {
        type: SchemaType.STRING,
        description: "A concise summary of the session and who it is for.",
      },
      learning_objectives: {
        type: SchemaType.ARRAY,
        items: { type: SchemaType.STRING },
        description: "Clear learning outcomes for the session.",
      },
      agenda: {
        type: SchemaType.ARRAY,
        items: { type: SchemaType.STRING },
        description: "Step-by-step agenda items in running order.",
      },
      materials: {
        type: SchemaType.ARRAY,
        items: { type: SchemaType.STRING },
        description: "Materials, props, handouts, and preparation notes.",
      },
      community_actions: {
        type: SchemaType.ARRAY,
        items: { type: SchemaType.STRING },
        description:
          "Practical next steps or community actions attendees can take after the session.",
      },
    },
    required: [
      "summary",
      "learning_objectives",
      "agenda",
      "materials",
      "community_actions",
    ],
  },
};

export function createStudyCompanionConfig(
  fileNames: string[],
  toolDeclarations: object[],
): LiveConfig {
  const fileList = fileNames.length
    ? `Uploaded climate references:\n${fileNames
        .map((fileName) => `- ${fileName}`)
        .join("\n")}`
    : "No uploaded references yet.";

  return {
    model: BASE_MODEL,
    generationConfig: {
      responseModalities: ["AUDIO"],
      thinkingConfig: {
        thinkingBudget: 0,
      },
    },
    realtimeInputConfig: {
      automaticActivityDetection: {
        silenceDurationMs: 400,
      },
    },
    systemInstruction: {
      parts: [
        {
          text: `You are the ${APP_NAME} library guide.

Your job is to help people learn from climate, sustainability, biodiversity, clean-energy, and Earth Day documents without inventing facts.

Rules:
1. Use uploaded references first and stay grounded in cited material.
2. Prefer concrete, source-aware answers over generic motivational language.
3. If the answer is missing from the uploaded material, say that clearly.
4. Suggest practical follow-up questions, comparisons, or sections to inspect next when helpful.
5. When the user asks to open a page, jump to a page, or show a specific section, prefer navigation to the source over a generic explanation.
6. When a relevant passage is found, use the highlight tool so the user can jump back to the source.
7. If a navigation request is ambiguous across multiple PDFs, ask which document to use.

Writing style:
- Calm, specific, and informative.
- No hype.
- No filler preamble.
- Keep explanations readable for mixed audiences such as students, volunteers, and community organizers.

${fileList}`,
        },
      ],
    },
    tools: toolDeclarations,
  };
}

export function createTeachInFacilitatorConfig(
  brief: TeachInBrief,
): LiveConfig {
  const sessionSummary = `Session title: ${brief.sessionTitle}
Audience: ${brief.audience}
Venue or context: ${brief.venueContext}
Duration: ${brief.duration}
Goals: ${brief.goals}
Focus areas: ${brief.focusAreas}
Available materials: ${brief.materials}`;

  return {
    model: BASE_MODEL,
    generationConfig: {
      responseModalities: ["AUDIO"],
      thinkingConfig: {
        thinkingBudget: 0,
      },
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: "Charon",
          },
        },
      },
    },
    realtimeInputConfig: {
      automaticActivityDetection: {
        silenceDurationMs: 400,
      },
    },
    systemInstruction: {
      parts: [
        {
          text: `You are the live facilitation coach inside ${APP_NAME}.

You help the user shape an Earth Day teach-in, climate literacy session, workshop, or community learning moment. Be practical, grounded, and collaborative.

Current session brief:
${sessionSummary}

Your behavior:
1. Start by confirming the session context in one or two sentences.
2. Ask focused follow-up questions only when a missing detail would materially change the plan.
3. Help refine tone, audience fit, and flow for a real event, not a generic classroom lesson.
4. Keep recommendations doable for community groups, schools, clubs, libraries, and informal gatherings.
5. When you have enough context or the user asks to finalize, call generate_facilitator_plan immediately.
6. The final plan should balance climate literacy, participation, and realistic action.

Prioritize:
- climate literacy
- sustainability and biodiversity awareness
- renewable energy and resilient communities
- environmental justice and local relevance
- concrete next steps attendees can take after the session

Avoid:
- interview language
- candidate scoring
- technical hiring framing
- generic corporate training tone`,
        },
      ],
    },
    tools: [{ functionDeclarations: [facilitatorPlanDeclaration] }],
  };
}
